import { normalizeMobileNumber } from '../config/keyRequest';

export const LOG_SOURCE = {
  UNLOC: 'unloc',
  ON_DEMAND: 'ondemand'
};

export const LOG_ACTION = {
  UNLOCKED: 'unlocked',
  LOCKED: 'locked',
  GRANTED: 'granted',
  DENIED: 'denied'
};

/** Dataverse choice values on oc_opencarelog.oc_logaction */
export const OC_LOG_ACTION = {
  UNLOCKED: 125160000,
  LOCKED: 125160001,
  GRANTED: 125160002,
  DENIED: 125160003
};

/** Dataverse choice values on oc_opencarelog.oc_logsource */
export const OC_LOG_SOURCE = {
  UNLOC: 125160000,
  ON_DEMAND: 125160001
};

function actionFromChoice(value) {
  switch (Number(value)) {
    case OC_LOG_ACTION.LOCKED:
      return LOG_ACTION.LOCKED;
    case OC_LOG_ACTION.GRANTED:
      return LOG_ACTION.GRANTED;
    case OC_LOG_ACTION.DENIED:
      return LOG_ACTION.DENIED;
    case OC_LOG_ACTION.UNLOCKED:
      return LOG_ACTION.UNLOCKED;
    default:
      return null;
  }
}

function sourceFromChoice(value) {
  switch (Number(value)) {
    case OC_LOG_SOURCE.ON_DEMAND:
      return LOG_SOURCE.ON_DEMAND;
    case OC_LOG_SOURCE.UNLOC:
      return LOG_SOURCE.UNLOC;
    default:
      return null;
  }
}

const ACTION_LABELS = {
  [LOG_ACTION.UNLOCKED]: 'Låst opp',
  [LOG_ACTION.LOCKED]: 'Låst',
  [LOG_ACTION.GRANTED]: 'Forespurt — innvilget',
  [LOG_ACTION.DENIED]: 'Forespurt — avvist'
};

const SOURCE_LABELS = {
  [LOG_SOURCE.UNLOC]: 'Unloc',
  [LOG_SOURCE.ON_DEMAND]: 'On-demand'
};

export function logActionLabel(action) {
  return ACTION_LABELS[action] || action || 'Ukjent';
}

export function logSourceLabel(source) {
  return SOURCE_LABELS[source] || source || 'Ukjent';
}

function parseJsonBody(value) {
  if (!value) {
    return {};
  }
  if (typeof value === 'object') {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
}

function toIso(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    const ms = value < 1e12 ? value * 1000 : value;
    const parsed = new Date(ms);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function eventTimeMs(iso) {
  if (!iso) {
    return null;
  }
  const parsed = new Date(iso).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

function extractUnlocActorPhone(event) {
  // access-events uses actorId; older /logs/access used appUserId.
  const raw =
    event?.actorId ||
    event?.appUserId ||
    event?.phoneNumber ||
    event?.phone ||
    event?.mobileNumber ||
    event?.appUser?.appUserId ||
    event?.appUser?.id ||
    '';
  if (raw == null || raw === '') {
    return '';
  }
  const text = String(raw).trim();
  if (!text || /^<?anonym/i.test(text)) {
    return '';
  }
  return normalizeMobileNumber(text) || text;
}

function extractUnlocActorName(event) {
  const raw =
    event?.actorName ||
    event?.appUserName ||
    event?.userName ||
    event?.appUser?.name ||
    '';
  const text = String(raw || '').trim();
  if (!text || text.toLowerCase() === 'unknown') {
    return '';
  }
  return text;
}

export function mapUnlocAccessEvent(event) {
  const timestamp = toIso(
    event.timestamp || event.created || event.createdAt || event.time || event.occurredAt
  );
  const appUserId = extractUnlocActorPhone(event);
  const action =
    String(event.action || '').toLowerCase() === 'locked' ? LOG_ACTION.LOCKED : LOG_ACTION.UNLOCKED;

  return {
    id: event.id ? `unloc-${event.id}` : `unloc-${timestamp}-${event.lockId}-${appUserId}`,
    source: LOG_SOURCE.UNLOC,
    action,
    timestamp,
    lockId: event.lockId || '',
    lockName: event.lockName || '',
    phoneNumber: appUserId,
    unlocUserName: extractUnlocActorName(event),
    keyId: event.keyId || '',
    confirmed: event.confirmed !== false,
    denialReason: null,
    durationMinutes: null,
    episodeId: null,
    userId: '',
    userEmail: '',
    userName: '',
    raw: event
  };
}

export function mapOpencareLogRecord(record) {
  const body = parseJsonBody(record.oc_jsonbody);
  const sourceHint =
    sourceFromChoice(record.oc_logsource) ||
    (String(body.source || '').toLowerCase() === 'accesscontrol'
      ? LOG_SOURCE.ON_DEMAND
      : String(body.source || '').toLowerCase() === 'unloc'
        ? LOG_SOURCE.UNLOC
        : null);
  const timestamp = toIso(
    record.oc_eventtime || body.checkedAt || body.timestamp || body.created || record.createdon
  );
  const phoneNumber =
    normalizeMobileNumber(record.oc_phonenumber || body.appUserId) ||
    record.oc_phonenumber ||
    '';
  const lockId = record.oc_lockid || body.lockId || record._oc_lock_value || '';
  const lockName = record.oc_lockdisplayname || body.lockName || '';
  const choiceAction = actionFromChoice(record.oc_logaction);

  const isUnloc =
    sourceHint === LOG_SOURCE.UNLOC ||
    body.action === 'unlocked' ||
    body.action === 'locked' ||
    choiceAction === LOG_ACTION.UNLOCKED ||
    choiceAction === LOG_ACTION.LOCKED;

  if (isUnloc && sourceHint !== LOG_SOURCE.ON_DEMAND) {
    const mapped = mapUnlocAccessEvent({
      id: String(record.oc_id || '').replace(/^unloc-/, '') || record.oc_opencarelogid,
      action: choiceAction || body.action,
      timestamp: record.oc_eventtime || body.timestamp || body.created || timestamp,
      keyId: body.keyId,
      lockId,
      lockName,
      appUserId: phoneNumber,
      appUserName: body.appUserName || body.actorName,
      actorName: body.actorName || body.appUserName,
      confirmed: body.confirmed,
      ...body
    });
    return {
      ...mapped,
      source: LOG_SOURCE.UNLOC,
      action: choiceAction || mapped.action,
      lockName: lockName || mapped.lockName,
      userId: record.oc_sessionuserid || body.sessionUserId || mapped.userId || '',
      userEmail: record.oc_sessionuseremail || body.sessionUserEmail || mapped.userEmail || '',
      userName:
        record.oc_sessionusername ||
        body.sessionUserName ||
        record.oc_employee_name ||
        mapped.userName ||
        '',
      unlocUserName: body.actorName || body.appUserName || mapped.unlocUserName || '',
      denialReason: record.oc_denialreason || body.denialReason || null
    };
  }

  const hasAccess =
    typeof body.hasAccess === 'boolean' ? body.hasAccess : Boolean(record.oc_authorized);
  const durationMinutes =
    body.keyData?.durationMinutes ?? body.durationMinutes ?? null;
  const action =
    choiceAction || (hasAccess ? LOG_ACTION.GRANTED : LOG_ACTION.DENIED);

  const stampedName = String(record.oc_sessionusername || '').trim();
  const employeeName = String(record.oc_employee_name || '').trim();
  // accesscontrol-flow often stamps oc_employee_name with the mobile number — not a person.
  const userName =
    stampedName ||
    (employeeName && !isPhoneNumberLikeName(employeeName, phoneNumber) ? employeeName : '');

  return {
    id: record.oc_opencarelogid || `ac-${record.oc_id || timestamp}`,
    source: LOG_SOURCE.ON_DEMAND,
    action,
    timestamp,
    lockId,
    lockName,
    phoneNumber,
    unlocUserName: '',
    keyId: '',
    confirmed: action !== LOG_ACTION.DENIED,
    denialReason: record.oc_denialreason || body.denialReason || null,
    durationMinutes: durationMinutes == null ? null : Number(durationMinutes),
    episodeId: null,
    userId: record.oc_sessionuserid || '',
    userEmail: record.oc_sessionuseremail || '',
    userName,
    raw: record
  };
}

export function mergeAccessLogRows(unlocEvents = [], opencareRecords = []) {
  const byId = new Map();

  for (const row of unlocEvents.map(mapUnlocAccessEvent)) {
    byId.set(row.id, row);
  }

  for (const row of opencareRecords.map(mapOpencareLogRecord)) {
    if (!byId.has(row.id)) {
      byId.set(row.id, row);
    }
  }

  return [...byId.values()].sort(
    (left, right) => (eventTimeMs(right.timestamp) || 0) - (eventTimeMs(left.timestamp) || 0)
  );
}

export function enrichLogsWithLocks(rows, locks = []) {
  const byId = new Map();
  for (const lock of locks) {
    const ids = [lock.lockId, lock.id, lock.externalId].filter(Boolean);
    for (const id of ids) {
      byId.set(String(id).toLowerCase(), lock);
    }
  }
  return rows.map((row) => {
    const lock = byId.get(String(row.lockId || '').toLowerCase());
    if (!lock) {
      return row;
    }
    return {
      ...row,
      lockName: row.lockName || lock.name || '',
      dataverseLockId: lock.lockId || lock.id || '',
      unlocLockId: lock.externalId || ''
    };
  });
}

/** Resolve Dataverse GUID and/or Unloc external id for a lock filter value. */
export function resolveLockFilterAliases(lockFilter, locks = []) {
  const needle = String(lockFilter || '').trim().toLowerCase();
  if (!needle) {
    return [];
  }
  const lock = locks.find((entry) => {
    const ids = [entry.lockId, entry.id, entry.externalId].filter(Boolean);
    return ids.some((id) => String(id).toLowerCase() === needle);
  });
  if (!lock) {
    return [String(lockFilter).trim()];
  }
  return [...new Set([lock.lockId, lock.id, lock.externalId].filter(Boolean).map(String))];
}

/**
 * Collect all known id aliases for a list of lock refs (group members + catalog locks).
 * Used when filtering hendelseslogg by tilgangsgruppe.
 */
export function collectLockFilterAliases(lockRefs = [], catalogLocks = []) {
  const aliases = new Set();
  for (const ref of lockRefs || []) {
    const ids = [ref.lockId, ref.id, ref.externalId].filter(Boolean);
    for (const id of ids) {
      aliases.add(String(id));
      for (const resolved of resolveLockFilterAliases(id, catalogLocks)) {
        aliases.add(String(resolved));
      }
    }
  }
  return [...aliases];
}

/** Intersection of two lock-id alias lists (case-insensitive). */
export function intersectLockFilterAliases(left = [], right = []) {
  if (!left.length || !right.length) {
    return [];
  }
  const rightSet = new Set(right.map((id) => String(id).toLowerCase()));
  return left.filter((id) => rightSet.has(String(id).toLowerCase()));
}

export function isGuiPlaceholderUserId(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return !normalized || normalized === 'opencare-gui';
}

/**
 * Build [logon, logoff) sessions per phone from oc_mobillog.
 * oc_direction: false/0 = Logon, true/1 = Logoff.
 */
export function buildPhoneSessions(mobilLogs = []) {
  const sorted = [...mobilLogs].sort(
    (left, right) => (eventTimeMs(left.timestamp) || 0) - (eventTimeMs(right.timestamp) || 0)
  );

  const openByPhone = new Map();
  const sessions = [];

  const closeOpen = (phone, endIso) => {
    const current = openByPhone.get(phone);
    if (!current) {
      return;
    }
    sessions.push({ ...current, end: endIso || null });
    openByPhone.delete(phone);
  };

  for (const log of sorted) {
    const phone = normalizeMobileNumber(log.mobileNumber) || log.mobileNumber;
    if (!phone) {
      continue;
    }

    const isLogoff = log.direction === true || log.direction === 1 || log.direction === 'true';
    const isLogon = log.direction === false || log.direction === 0 || log.direction === 'false';

    if (isLogon) {
      closeOpen(phone, log.timestamp);
      openByPhone.set(phone, {
        phoneNumber: phone,
        start: log.timestamp,
        userId: log.userId || '',
        userEmail: log.email || '',
        userName: log.name || ''
      });
      continue;
    }

    if (isLogoff) {
      closeOpen(phone, log.timestamp);
    }
  }

  for (const open of openByPhone.values()) {
    sessions.push({ ...open, end: null });
  }

  return sessions;
}

export function attachSessionUsers(rows, sessions = []) {
  return rows.map((row) => {
    const phone = normalizeMobileNumber(row.phoneNumber) || row.phoneNumber;
    const hasRealUser =
      Boolean(row.userId || row.userEmail) ||
      (Boolean(row.userName) && !isPhoneNumberLikeName(row.userName, phone));
    // Prefer real session stamp on oc_opencarelog; ignore phone-number placeholders.
    if (hasRealUser) {
      return row;
    }

    const at = eventTimeMs(row.timestamp);
    if (at == null) {
      return row;
    }
    const match = sessions.find((session) => {
      if ((normalizeMobileNumber(session.phoneNumber) || session.phoneNumber) !== phone) {
        return false;
      }
      const start = eventTimeMs(session.start);
      if (start == null) {
        return false;
      }
      const end = session.end ? eventTimeMs(session.end) : Number.POSITIVE_INFINITY;
      return at >= start && at < (end ?? Number.POSITIVE_INFINITY);
    });

    if (!match) {
      return {
        ...row,
        // Clear placeholder mobile-as-name so UI does not show it as "pålogget bruker".
        userName: isPhoneNumberLikeName(row.userName, phone) ? '' : row.userName
      };
    }

    return {
      ...row,
      userId: match.userId || row.userId,
      userEmail: match.userEmail || row.userEmail,
      userName: match.userName || row.unlocUserName || ''
    };
  });
}

function isPhoneNumberLikeName(name, mobile) {
  const raw = String(name || '').trim();
  if (!raw) {
    return true;
  }
  const normalizedName = normalizeMobileNumber(raw) || raw;
  const normalizedMobile = normalizeMobileNumber(mobile) || String(mobile || '').trim();
  if (normalizedMobile && normalizedName === normalizedMobile) {
    return true;
  }
  if (/^\+?\d{6,}$/.test(raw)) {
    return true;
  }
  // Device labels like "97550225 Sentrum" (phone digits + place), not a person.
  const firstToken = raw.split(/\s+/)[0] || '';
  if (/^\+?\d{6,}$/.test(firstToken)) {
    const tokenDigits = firstToken.replace(/\D/g, '');
    const mobileDigits = String(normalizedMobile || '').replace(/\D/g, '');
    if (!mobileDigits) {
      return true;
    }
    return mobileDigits.endsWith(tokenDigits) || tokenDigits.endsWith(mobileDigits.slice(-8));
  }
  return false;
}

/**
 * When no real session user is known, fall back to oc_telefon register name
 * for "Pålogget bruker" (same for Unloc and On-demand).
 * Never overwrites a real session user (id/email/person name).
 */
export function enrichLogsWithPhones(rows, phones = []) {
  const byMobile = new Map();
  for (const phone of phones || []) {
    const mobile = normalizeMobileNumber(phone.mobileNumber || phone.oc_mobilnummer);
    const name = String(phone.name || phone.oc_name || '').trim();
    if (mobile && name && !isPhoneNumberLikeName(name, mobile)) {
      byMobile.set(mobile, name);
    }
  }
  if (!byMobile.size) {
    return rows;
  }

  return rows.map((row) => {
    const mobile = normalizeMobileNumber(row.phoneNumber) || row.phoneNumber;
    if (!mobile) {
      return row;
    }
    const registerName = byMobile.get(mobile);
    if (!registerName) {
      return row;
    }
    const hasRealUser =
      Boolean(row.userId || row.userEmail) ||
      (Boolean(row.userName) && !isPhoneNumberLikeName(row.userName, mobile));
    if (hasRealUser) {
      return row;
    }
    const unlocName = String(row.unlocUserName || '').trim();
    if (unlocName && !isPhoneNumberLikeName(unlocName, mobile)) {
      return row;
    }
    return {
      ...row,
      // sessionUserLabel falls back to unlocUserName — used for both Unloc and On-demand.
      unlocUserName: registerName
    };
  });
}

export function linkOnDemandEpisodes(rows) {
  const unlocUnlocks = rows
    .filter((row) => row.source === LOG_SOURCE.UNLOC && row.action === LOG_ACTION.UNLOCKED)
    .sort((left, right) => (eventTimeMs(left.timestamp) || 0) - (eventTimeMs(right.timestamp) || 0));

  const usedUnlockIds = new Set();

  const withGrants = rows.map((row) => {
    if (row.source !== LOG_SOURCE.ON_DEMAND || row.action !== LOG_ACTION.GRANTED) {
      return row;
    }

    const grantedAt = eventTimeMs(row.timestamp);
    if (grantedAt == null) {
      return row;
    }
    const windowMs = Math.max(Number(row.durationMinutes) || 60, 1) * 60 * 1000;
    const phone = normalizeMobileNumber(row.phoneNumber) || row.phoneNumber;

    const unlock = unlocUnlocks.find((candidate) => {
      if (usedUnlockIds.has(candidate.id)) {
        return false;
      }
      const candidatePhone = normalizeMobileNumber(candidate.phoneNumber) || candidate.phoneNumber;
      if (candidate.lockId !== row.lockId || candidatePhone !== phone) {
        return false;
      }
      const openedAt = eventTimeMs(candidate.timestamp);
      if (openedAt == null) {
        return false;
      }
      return openedAt >= grantedAt && openedAt <= grantedAt + windowMs;
    });

    if (!unlock) {
      return row;
    }

    usedUnlockIds.add(unlock.id);
    return { ...row, episodeId: unlock.id };
  });

  return withGrants.map((row) => {
    if (row.source !== LOG_SOURCE.UNLOC || !usedUnlockIds.has(row.id)) {
      return row;
    }
    return { ...row, episodeId: row.id };
  });
}

export function filterAccessLogs(
  rows,
  { source, lockId, lockIds, phone, user, from, to, search } = {}
) {
  const phoneNorm = phone ? normalizeMobileNumber(phone) || phone : '';
  // GUI placeholder user ids must not hide Unloc events without a session user.
  const userNeedle = isGuiPlaceholderUserId(user)
    ? ''
    : String(user || '').trim().toLowerCase();
  const searchNeedle = String(search || '').trim().toLowerCase();
  const fromMs = from ? eventTimeMs(from) : null;
  const toMs = to ? eventTimeMs(to) : null;
  const lockNeedles = (lockIds?.length
    ? lockIds
    : lockId
      ? [lockId]
      : []
  ).map((id) => String(id).trim().toLowerCase()).filter(Boolean);

  const phoneDigits = phoneNorm
    ? (normalizeMobileNumber(phoneNorm) || phoneNorm).replace(/\D/g, '')
    : '';

  return rows.filter((row) => {
    if (source && source !== 'all' && row.source !== source) {
      return false;
    }
    if (lockNeedles.length) {
      const rowLock = String(row.lockId || '').toLowerCase();
      const aliases = [
        rowLock,
        String(row.dataverseLockId || '').toLowerCase(),
        String(row.unlocLockId || '').toLowerCase()
      ].filter(Boolean);
      if (!lockNeedles.some((needle) => aliases.includes(needle))) {
        return false;
      }
    }
    if (phoneDigits) {
      const rowDigits = (
        normalizeMobileNumber(row.phoneNumber) ||
        row.phoneNumber ||
        ''
      ).replace(/\D/g, '');
      if (!rowDigits) {
        return false;
      }
      const same =
        rowDigits === phoneDigits ||
        (rowDigits.length >= 8 &&
          phoneDigits.length >= 8 &&
          rowDigits.slice(-8) === phoneDigits.slice(-8));
      if (!same) {
        return false;
      }
    }
    if (userNeedle) {
      const haystack = `${row.userId} ${row.userEmail} ${row.userName} ${row.unlocUserName}`.toLowerCase();
      if (!haystack.includes(userNeedle)) {
        return false;
      }
    }
    const at = eventTimeMs(row.timestamp);
    // Keep rows with missing/invalid timestamps — do not drop Unloc data due to parse issues.
    if (at != null) {
      if (fromMs != null && at < fromMs) {
        return false;
      }
      if (toMs != null && at > toMs) {
        return false;
      }
    }
    if (searchNeedle) {
      const haystack = [
        row.lockName,
        row.lockId,
        row.phoneNumber,
        row.userName,
        row.userEmail,
        row.userId,
        row.denialReason,
        logActionLabel(row.action)
      ]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(searchNeedle)) {
        return false;
      }
    }
    return true;
  });
}

export function sessionUserLabel(row) {
  // Prefer stamped Session user name / employee (session → oc_telefon). Unloc actorName only for live API rows.
  return row.userName || row.userEmail || row.userId || row.unlocUserName || '';
}
